import { getSession, supabase } from '@app-studio-web/ui/helpers/supabase'
import * as Sentry from '@sentry/react'
import i18n from 'i18next'

type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2'

// Track successful calls by user-language combination
const successfulCalls = new Set<string>()

const normalizeLanguageCode = (value: string): string => {
  const normalized = value.trim().toLowerCase().split('-')[0] || ''
  if (normalized === 'ch') return 'zh'
  if (normalized === 'in') return 'id'
  return normalized
}

const mapComfortLevelToCefr = (value: string | null): CefrLevel => {
  if (!value) return 'A1'

  const normalized = value.trim().toLowerCase()
  if (normalized === 'beginner') return 'A1'
  if (normalized === 'elementary' || normalized === 'basic') return 'A2'
  if (normalized === 'intermediate') return 'B1'
  if (normalized === 'advanced' || normalized === 'confident') return 'B2'

  if (normalized.includes('beginner')) return 'A1'
  if (normalized.includes('elementary') || normalized.includes('basic'))
    return 'A2'
  if (normalized.includes('intermediate')) return 'B1'
  if (normalized.includes('advanced') || normalized.includes('confident'))
    return 'B2'

  return 'A1'
}

export async function saveOnboardingAnswers() {
  try {
    const { data: sessionData } = await getSession()
    const userId = sessionData?.session?.user?.id

    if (!userId) {
      console.error('saveOnboardingAnswers: No user ID found')
      return { success: false }
    }

    // Get selected onboarding language
    const selectedLanguage =
      normalizeLanguageCode(localStorage.getItem('selectedLanguage') || 'en') ||
      'en'
    const comfortLevel = localStorage.getItem('comfortLevel')
    const userLevel = mapComfortLevelToCefr(comfortLevel)

    // Create unique key for this user-language combination
    const callKey = `${userId}-${selectedLanguage}`

    // Prevent multiple calls for the same user-language combination
    if (successfulCalls.has(callKey)) {
      console.log(
        'saveOnboardingAnswers: Function already called successfully for this user-language combination, skipping',
      )
      return { success: true }
    }

    // Get user's current app language from i18n
    const userLanguage = i18n.language || 'en'

    const { error: createError } = await supabase.functions.invoke(
      'lingochatai-create-new-language',
      {
        body: {
          user_language: userLanguage,
          user_id: userId,
          targeted_language_code: selectedLanguage,
          user_level: userLevel,
        },
      },
    )

    if (createError) {
      console.error(
        'saveOnboardingAnswers: Supabase function error',
        createError,
      )
      Sentry.captureException(createError)
      return { success: false }
    }

    // Mark this user-language combination as successfully called
    successfulCalls.add(callKey)
    console.log(
      'saveOnboardingAnswers: Successfully called for user-language combination:',
      callKey,
    )
    return { success: true }
  } catch (error) {
    console.error('saveOnboardingAnswers: Unexpected error', error)
    Sentry.captureException(error)
    return { success: false }
  }
}
